import { useClients } from '@entities/clients';
import { useSelectedClient } from '@entities/clients/heplers.ts';
import {
  useCreateClientMutation,
  useGetClientByIdQuery,
  useUpdateClientMutation,
} from '@entities/clients/rtk-query.ts';
import { yupResolver } from '@hookform/resolvers/yup';
import type { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { format, parse } from 'date-fns';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import * as yup from 'yup';
import { base64ToBlob, capitalizeString } from '@shared/helpers/index.ts';
import { usePopup } from '@shared/hooks';
import type { Group } from '@shared/types/common';
import type { ContactItem } from '@shared/types/contacts';

export interface IClientForm {
  photo: File | string | null;
  last_name: string;
  first_name: string;
  middle_name: string;
  birth_date: Date | null;
  gender: string;
  phonenumber: string;
  email: string;
  custom_groups: Group[];
  description: string;
  color_tag: string;
  active_contacts: Pick<ContactItem, 'type' | 'value' | 'id'>[];
}

const schema = yup
  .object({
    // last_name: yup.string().required('Необходимо заполнить фамилию'),
    first_name: yup.string().required('Необходимо заполнить имя'),
    phonenumber: yup.string().required('Необходимо заполнить телефон'),
    // email: yup
    // .string()
    // .required('Необходимо указать почту')
    // .matches(/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/i, 'E-mail указан неверно'),
    // gender: yup.string().required('Укажите пол'),
    // photo: yup.mixed().required(),
  })
  .required();

interface Props {
  selected?: number;
  onSuccess?: () => void;
  skipQuery?: boolean;
}

export const useClientForm = ({ selected, onSuccess, skipQuery }: Props) => {
  const { clientId } = useParams();
  const { addClient, setClient } = useClients();
  const { setSelectedClient } = useSelectedClient();

  const { data: client, isLoading: isClientLoading } = useGetClientByIdQuery(Number(clientId), {
    skip: skipQuery,
  });
  const { popup } = usePopup();
  const [isLoading, setIsLoading] = useState(false);
  const [createClientMutation] = useCreateClientMutation();
  const [updateClientMutation] = useUpdateClientMutation();
  const { ...methods } = useForm<IClientForm>({
    resolver: yupResolver(schema as any),
    defaultValues: {
      last_name: '',
      first_name: '',
      middle_name: '',
      phonenumber: '',
      email: '',
      birth_date: null,
      gender: '',
      photo: null,
      custom_groups: [],
      description: '',
      color_tag: '',
      active_contacts: [],
    },
    shouldFocusError: false,
  });
  const createFormData = (data: IClientForm) => {
    const clientData = new FormData();
    if (!client) clientData.append('phonenumber', data.phonenumber);

    const stringValues = ['gender', 'email', 'description'];
    data.description && clientData.append('description', data.description);
    data?.last_name && clientData.append('last_name', capitalizeString(data.last_name));
    data?.first_name && clientData.append('first_name', capitalizeString(data.first_name));
    data?.middle_name && clientData.append('middle_name', capitalizeString(data.middle_name));

    if (typeof data?.photo === 'string') {
      clientData.append('photo', base64ToBlob(data.photo), 'photo.png');
    } else {
      clientData.append('photo', '');
    }

    clientData.append(`active_contacts`, JSON.stringify(data.active_contacts));

    let field: keyof IClientForm;

    for (field in methods.formState.dirtyFields) {
      if (stringValues.indexOf(field) !== -1) {
        clientData.append(field, data[field] as string);
      }

      if (field === 'birth_date' && data.birth_date)
        clientData.append('birth_date', format(data.birth_date, 'yyyy-MM-dd'));

      if (field === 'custom_groups') {
        const ids: string[] = data.custom_groups.map((group: Group) => {
          return group.id.toString();
        });
        clientData.append('custom_groups', JSON.stringify(ids));
      }
    }

    return clientData;
  };

  const submit = async (data: IClientForm) => {
    setIsLoading(true);
    let message = '';

    const formData = createFormData(data);

    if (client) {
      const response = await updateClientMutation({ id: client.id, body: formData });
      if (response?.error) {
        message = 'обновить';
        popup({ type: 'error', text: `Не удалось ${message} клиента` });
        const err = response.error as FetchBaseQueryError;
        if (err?.status === 400) {
          const errorMessages = { ...(err.data as any) };
          for (const message in errorMessages) {
            if (Object.prototype.hasOwnProperty.call(errorMessages, message)) {
              const text = { message: errorMessages[message][0] };
              methods.setError(message as any, text);
            }
          }
        }
      } else {
        message = 'обновлен';

        setClient(response.data);
        // console.log(response.data.id);

        popup({ type: 'success', text: `Клиент успешно ${message}` });
        onSuccess?.();
      }
    } else {
      const response = await createClientMutation(formData);
      if (response?.error) {
        const err = response.error as FetchBaseQueryError;
        message = 'создать';
        if (err?.status === 400) {
          popup({ type: 'error', text: `Не удалось ${message} клиента` });
          const errorMessages = { ...(err.data as any) };
          for (const message in errorMessages) {
            if (Object.prototype.hasOwnProperty.call(errorMessages, message)) {
              const text = { message: errorMessages[message][0] };
              methods.setError(message as any, text);
            }
          }
        }
      } else {
        message = 'создан';
        response?.data && addClient(response?.data);
        response?.data && setSelectedClient(response?.data);
        popup({ type: 'success', text: `Клиент успешно ${message}` });
        onSuccess?.();
      }
    }
    setIsLoading(false);
  };

  React.useEffect(() => {
    if (client) {
      methods.reset({
        photo: client?.photo,
        last_name: client?.last_name,
        first_name: client?.first_name,
        middle_name: client?.middle_name,
        gender: client?.gender,
        birth_date: parse(client?.birth_date, 'yyyy-MM-dd', new Date()),
        phonenumber: client?.phonenumber,
        email: client?.email,
        description: client?.description || '',
        custom_groups: client?.user.custom_groups,
        active_contacts:
          client?.active_contacts?.map((contact) => ({
            id: contact.id,
            type: contact.type,
            value: contact.value,
          })) || [],
      });
    }
  }, [client]);

  return {
    // 	register,
    // handleSubmit,
    // 	control,
    // 	errors,
    submit,
    isLoading,
    isClientLoading,
    ...methods,
  };
};
